import React, {useState} from "react";
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import {Building2, Info, Pencil, Plus, Search, Server, Trash2, X} from "lucide-react";
import {getBankIntegradoPalette} from "../financeiroUtils";

const createInitialValues = (bank) => ({
    codigo: bank?.codigo || '',
    nomeComercial: bank?.nomeComercial || '',
    endpointHomologacao: bank?.endpointHomologacao || '',
    endpointProducao: bank?.endpointProducao || '',
    integrado: Boolean(bank?.integrado),
    emissaoBoletoOnline: Boolean(bank?.emissaoBoletoOnline)
});

const inputSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 2,
        bgcolor: '#fff',
    },
};

const sectionTitleSx = {fontSize: '1rem', color: '#374151', fontWeight: 600};

function BankDialog({bank, open, saving, onClose, onSave}) {
    const [values, setValues] = useState(createInitialValues(bank));
    const [errors, setErrors] = useState({});

    React.useEffect(() => {
        setValues(createInitialValues(bank));
        setErrors({});
    }, [bank, open]);

    React.useEffect(() => {
        if (!values.integrado){
            setValues((current) => ({...current, emissaoBoletoOnline: false}))
        }
    }, [values.integrado]);

    React.useEffect(() => {
        if (!values.emissaoBoletoOnline){
            setValues((current) => ({...current, endpointHomologacao: '', endpointProducao: ''}))
        }
    }, [values.emissaoBoletoOnline]);

    const handleChange = (field) => (event) => {
        const nextValue = event?.target?.type === 'checkbox' ? event.target.checked : event.target.value;
        setValues((current) => ({...current, [field]: nextValue}));
    };

    const validate = () => {
        const nextErrors = {};

        if (!values.codigo.trim()) nextErrors.codigo = 'Código do banco é obrigatório';
        if (!values.nomeComercial.trim()) nextErrors.nomeComercial = 'Nome comercial é obrigatório';
        if (values.emissaoBoletoOnline === true && !values.endpointHomologacao.trim()) nextErrors.endpointHomologacao = 'O endpoint de homologação da API do banco é obrigatória';
        if (values.emissaoBoletoOnline === true && !values.endpointProducao.trim()) nextErrors.endpointProducao = 'O endpoint da API do banco é obrigatória';

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        const success = await onSave?.(values, bank?.id);
        if (success) onClose?.();
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md" PaperProps={{sx: {borderRadius: 3, maxHeight: '90vh'}}}>
            <DialogTitle sx={{p: 0}}>
                <Box sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', p: 3, borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, bgcolor: '#fff', zIndex: 1}}>
                    <Box>
                        <Typography sx={{fontSize: '1.5rem', color: '#111827', fontWeight: 700}}>
                            {bank ? 'Editar Banco' : 'Novo Banco'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{mt: 0.75}}>
                            Configure os dados do banco e parametrização para emissão de boletos
                        </Typography>
                    </Box>
                    <Button onClick={onClose} disabled={saving} sx={{minWidth: 0, p: 1, borderRadius: 2, color: '#6b7280'}}>
                        <X size={18} />
                    </Button>
                </Box>
            </DialogTitle>

            <DialogContent dividers sx={{p: 3}}>
                <Stack spacing={4}>
                    <Box>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 2.5, color: '#374151'}}>
                            <Building2 size={18} />
                            <Typography sx={sectionTitleSx}>Informações do Banco</Typography>
                        </Stack>

                        <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: 'repeat(2, 1fr)'}, gap: 2}}>
                            <TextField
                                label="Nome do Banco"
                                value={values.nomeComercial}
                                onChange={handleChange('nomeComercial')}
                                error={Boolean(errors.nomeComercial)}
                                helperText={errors.nomeComercial}
                                placeholder="Ex: Banco do Brasil"
                                sx={{...inputSx, gridColumn: {md: 'span 2'}}}
                            />

                            <TextField
                                label="Código"
                                value={values.codigo}
                                onChange={handleChange('codigo')}
                                error={Boolean(errors.codigo)}
                                helperText={errors.codigo}
                                placeholder="Ex: 001"
                                sx={inputSx}
                            />
                        </Box>
                    </Box>

                    <Box sx={{pt: 3, borderTop: '1px solid #e5e7eb'}}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 2.5, color: '#374151'}}>
                            <Server size={18} />
                            <Typography sx={sectionTitleSx}>Configuração da API</Typography>
                        </Stack>

                        <Box sx={{p: 2, borderRadius: 2, border: '1px solid #bfdbfe', bgcolor: '#eff6ff', mb: 2.5}}>
                            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                <Info size={18} color="#2563eb" style={{marginTop: 2, flexShrink: 0}} />
                                <Box>
                                    <Typography sx={{fontSize: 14, color: '#1d4ed8', fontWeight: 700, mb: 0.5}}>Integração Banco do Brasil</Typography>
                                    <Typography variant="body2" sx={{color: '#2563eb'}}>
                                        Configure as credenciais de acesso à API do banco para geração automática de boletos.
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>

                        <Stack spacing={2}>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.25, border: '1px solid #d1d5db', borderRadius: 2, bgcolor: '#fff'}}>
                                <Switch checked={Boolean(values.integrado) === true} onChange={(event) => setValues((current) => ({...current, integrado: event.target.checked}))} />
                                <Typography sx={{fontSize: 14, color: '#374151'}}>Este banco está integrado para emissão de Boleto?</Typography>
                            </Box>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.25, border: '1px solid #d1d5db', borderRadius: 2, bgcolor: '#fff'}}>
                                <Switch disabled={!values.integrado} checked={Boolean(values.emissaoBoletoOnline) === true} onChange={(event) => setValues((current) => ({...current, emissaoBoletoOnline: event.target.checked}))} />
                                <Typography sx={{fontSize: 14, color: '#374151'}}>A emissão dos boletos é online (via API do Banco)?</Typography>
                            </Box>
                            <TextField disabled={!values.emissaoBoletoOnline} label="Host da API de Homologação" value={values.endpointHomologacao} onChange={handleChange('endpointHomologacao')} placeholder="https://api.hom.bb.com.br" sx={inputSx} />
                            <TextField disabled={!values.emissaoBoletoOnline} label="Host da API de Produção" value={values.endpointProducao} onChange={handleChange('endpointProducao')} placeholder="https://api.bb.com.br" sx={inputSx} />
                        </Stack>
                    </Box>
                </Stack>
            </DialogContent>

            <DialogActions sx={{display: 'flex', justifyContent: 'space-between', px: 3, py: 2.5, borderTop: '1px solid #e5e7eb'}}>
                <Stack direction="row" spacing={1.5}>
                    <Button onClick={onClose} disabled={saving} sx={{border: '1px solid #d1d5db', color: '#374151'}}>
                        Cancelar
                    </Button>
                    <Button variant="contained" onClick={handleSubmit} disabled={saving} sx={{bgcolor: '#0f766e', '&:hover': {bgcolor: '#115e59'}}}>
                        {bank ? 'Salvar Alterações' : 'Cadastrar Banco'}
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
}

export default function FinanceiroBankTab({
    items,
    pagination,
    loading,
    saving,
    search,
    page,
    onChangeSearch,
    onChangePage,
    onSave,
    onDelete,
}) {
    const [editing, setEditing] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);

    const openDialog = (account = null) => {
        setEditing(account);
        setDialogOpen(true);
    };

    return (
        <Stack spacing={3}>
            <Stack direction={{xs: 'column', md: 'row'}} spacing={1.5}>
                <TextField
                    value={search}
                    onChange={(event) => onChangeSearch?.(event.target.value)}
                    placeholder="Buscar por código ou nome do banco..."
                    fullWidth
                    InputProps={{startAdornment: <Search size={18} color="#9ca3af" style={{marginRight: 8}} />}}
                />
                <Button variant="contained" startIcon={<Plus size={18} />} onClick={() => openDialog()} sx={{bgcolor: '#0f766e', '&:hover': {bgcolor: '#115e59'}, whiteSpace: 'nowrap'}}>
                    Novo cadastro de banco
                </Button>
            </Stack>

            <Paper variant="outlined" sx={{borderRadius: 3, overflow: 'hidden', borderColor: '#e5e7eb'}}>
                <Table>
                    <TableHead sx={{bgcolor: '#f9fafb'}}>
                        <TableRow>
                            <TableCell>Banco</TableCell>
                            <TableCell>Integração</TableCell>
                            <TableCell>Emissão</TableCell>
                            <TableCell align="right">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!items.length ? (
                            <TableRow>
                                <TableCell colSpan={4}>
                                    <Typography color="text.secondary" sx={{py: 3, textAlign: 'center'}}>
                                        Nenhum banco encontrado.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : items.map((item) => {
                            const palette = getBankIntegradoPalette(item.integrado);
                            return (
                                <TableRow key={item.id} hover>
                                    <TableCell>
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <Box sx={{width: 40, height: 40, borderRadius: 2, bgcolor: '#ccfbf1', color: '#0f766e', display: 'grid', placeItems: 'center'}}>
                                                <Building2 size={20} />
                                            </Box>
                                            <Box>
                                                <Typography sx={{color: '#111827', fontWeight: 600}}>{item.nomeComercial || '-'}</Typography>
                                                <Typography variant="body2" color="text.secondary">{item.codigo || '-'}</Typography>
                                            </Box>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={item.integrado ? 'Controle Financeiro + Boleto' : 'Controle Financeiro'} size="small" sx={{bgcolor: palette.bg, color: palette.color, border: `1px solid ${palette.border}`}} />
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={item.emissaoBoletoOnline ? 'Boleto Online' : 'Troca de Arquivos'} size="small" sx={{bgcolor: palette.bg, color: palette.color, border: `1px solid ${palette.border}`}} />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                                            <Button variant="outlined" startIcon={<Pencil size={16} />} onClick={() => openDialog(item)}>Editar</Button>
                                            <Button color="error" variant="outlined" startIcon={<Trash2 size={16} />} onClick={() => onDelete?.(item.id)} disabled={saving}>Excluir</Button>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </Paper>

            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                    Mostrando {(Number(page || 0) * 10) + (items.length ? 1 : 0)} a {Math.min((Number(page || 0) * 10) + items.length, pagination?.total || items.length)} de {pagination?.total || items.length} bancos
                </Typography>
                <Stack direction="row" spacing={1}>
                    <Button variant="outlined" disabled={!pagination?.has_previous} onClick={() => onChangePage?.(Math.max(0, Number(page || 0) - 1))} sx={{minWidth: 44, px: 1.5}}>
                        Anterior
                    </Button>
                    <Button variant="outlined" disabled={!pagination?.has_next} onClick={() => onChangePage?.(Number(page || 0) + 1)} sx={{minWidth: 44, px: 1.5}}>
                        Próxima
                    </Button>
                </Stack>
            </Stack>

            <BankDialog
                open={dialogOpen}
                bank={editing}
                saving={saving || loading}
                onClose={() => setDialogOpen(false)}
                onSave={onSave}
            />
        </Stack>
    );
}
